import { FormEvent, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import posthog from "posthog-js";
import { demoInviteService } from "../../api/demoInviteService";
import { track } from "../../lib/posthog";

export default function DemoAccept() {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => (search.get("token") || "").trim(), [search]);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const extractApiErrorMessage = (err: unknown, fallback: string): string => {
    const axiosErr = err as {
      response?: { data?: { message?: string; error?: string } };
    };
    return (
      axiosErr?.response?.data?.message ??
      axiosErr?.response?.data?.error ??
      (err instanceof Error ? err.message : fallback)
    );
  };

  useEffect(() => {
    if (!token) return;
    try {
      posthog.identify(token);
    } catch {
      // ignore
    }
    track("demo_landing_viewed", { token });
  }, [token]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setLoading(true);
    setError(null);
    track("demo_accept_submitted");
    try {
      await demoInviteService.accept({ token, email: email.trim().toLowerCase() });
      track("demo_accept_succeeded");
      navigate("/login");
    } catch (err) {
      track("demo_accept_failed");
      setError(extractApiErrorMessage(err, "Failed to accept invite."));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return <div className="p-6 text-white">Missing invite token.</div>;
  }

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center p-6">
      <form onSubmit={submit} className="w-full max-w-md rounded-xl border border-slate-700 bg-slate-900/70 p-6 space-y-4">
        <h1 className="text-xl font-semibold">Accept demo invite</h1>
        <p className="text-sm text-slate-300">Confirm your email to activate access.</p>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          className="w-full rounded-md border border-slate-600 bg-slate-800 px-3 py-2"
        />
        {error && <p className="text-sm text-red-300">{error}</p>}
        <button
          type="submit"
          disabled={loading}
          className="w-full rounded-md bg-blue-600 px-3 py-2 font-medium disabled:opacity-60"
        >
          {loading ? "Confirming..." : "Confirm and go to login"}
        </button>
      </form>
    </div>
  );
}
